import { ComponentProps, FC, ReactNode } from 'react';
import { Stack } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';

import { MainButton } from '~components/atoms/MainButton';
import { LocalizationTypography } from '~components/organisms/localization';
import { useStripePromise } from '~hooks/payment/useStripePromise';
import { useTenantContext } from '~providers/TenantProvider';

type Props = {
  options?: ComponentProps<typeof Elements>['options'];
  children: ReactNode;
};

const PaymentUnavailable: FC = () => {
  const { isRefetching, refetch } = useTenantContext();

  return (
    <Stack gap={1.5} alignItems="center" textAlign="center" sx={{ py: 3 }}>
      <LocalizationTypography
        textId="app.rentalPage.paymentUnavailable.title"
        defaultText="Payments are not available right now"
        variant="Poppins/Subheading-md"
      />
      <LocalizationTypography
        textId="app.rentalPage.paymentUnavailable.description"
        defaultText="Please try again in a moment."
        variant="Inter/Body-sm"
        color="text.secondary"
      />
      <MainButton
        variant="outlined"
        fullWidth
        isLoading={isRefetching}
        onClick={refetch}
      >
        <LocalizationTypography
          textId="app.rentalPage.paymentUnavailable.retry"
          defaultText="Try again"
          variant="Poppins/Subheading-md"
        />
      </MainButton>
    </Stack>
  );
};

export const StripeElements: FC<Props> = ({ options, children }) => {
  const stripePromise = useStripePromise();

  if (!stripePromise) {
    return <PaymentUnavailable />;
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
};
